import { Button, IconButton } from '@creditornot/cb-button';
import { Calendar, Settings } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Popover } from '@creditornot/cb-popover';
import { Interval } from 'luxon';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { typographyCss } from 'common/typography';

import FilterDateRangePicker from './FilterDateRangePicker';
import { InsightSettings } from './InsightSettings';
import { useInsightSubRoutes } from '../hooks/useInsightsSubRoutes';
import { useIsReportsEnabled } from '../hooks/useIsReportsEnabled';
import { DateRange, InsightEntity } from '../types';
import { getPrevDateInterval } from '../utils';

type InsightPageHeaderProps = {
  insightEntity: InsightEntity;
  dateInterval: Interval;
  dateRange: DateRange;
  isVATEnabled: boolean;
  setIsVATEnabled: (isVATEnabled: boolean) => void;
  setDateRange: (dateRange: DateRange) => void;
};

const InsightPageControlsContainer = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;

  @media (width <= ${MOBILE_BREAKPOINT}px) {
    justify-content: space-between;
    flex-direction: column;
  }
`;

const VenueCount = styled.div`
  ${typographyCss.Body3}
  color: ${cssThemedValue('textSecondary')};
  padding-block: 8px;
`;

const RightControls = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 16px;
  align-self: stretch;
`;

const StyledInput = styled(Input)`
  min-width: 217px;
`;

const PreviousPeriod = styled.span`
  ${typographyCss.Small}
  color: ${cssThemedValue('textSecondary')};
`;

const DateRoot = styled.div`
  justify-content: center;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    align-items: flex-start;
  }
`;

const formatDateInterval = (dateInterval: Interval, locale: string) =>
  `${dateInterval.start.setLocale(locale).toLocaleString()} - ${dateInterval.end.setLocale(locale).toLocaleString()}`;

export const InsightPageControls = ({
  insightEntity,
  dateRange,
  dateInterval,
  isVATEnabled,
  setIsVATEnabled,
  setDateRange,
}: InsightPageHeaderProps) => {
  const venueCount = null;
  const { locale } = useIntl();
  const history = useHistory();
  const { isLoading, isReportsEnabled } = useIsReportsEnabled(insightEntity.type);
  const previousDateInterval = getPrevDateInterval(dateInterval);

  const { formattedDateRange, formattedPreviousDateRange } = useMemo(
    () => ({
      formattedDateRange: formatDateInterval(dateInterval, locale),
      formattedPreviousDateRange: previousDateInterval && formatDateInterval(previousDateInterval, locale),
    }),
    [dateInterval, previousDateInterval, locale],
  );
  const {
    reports: { indexLink: reportLink, newLink: newReportLink },
  } = useInsightSubRoutes();
  const reportIndexPathName = reportLink(history.location).pathname;
  const isControlsEnabled = reportIndexPathName && !history.location.pathname.startsWith(reportIndexPathName);

  const handleNavigateToCreateReport = () => {
    history.push(newReportLink(history.location));
  };

  return (
    <>
      {isControlsEnabled && (
        <InsightPageControlsContainer>
          {insightEntity.type !== 'venue' && venueCount !== null && (
            <VenueCount>
              <FormattedMessage
                id="insights.showing-aggregate-for-venues"
                values={{
                  venueCount,
                }}
              />
            </VenueCount>
          )}
          <Popover
            content={<FilterDateRangePicker onChange={setDateRange} value={dateRange} locale={locale} />}
            showOnClick
            showOnHover={false}
            placement="bottom"
          >
            <DateRoot>
              <div>
                <StyledInput icon={<Calendar />} value={formattedDateRange} readOnly />
              </div>
              {formattedPreviousDateRange && (
                <PreviousPeriod>
                  <FormattedMessage
                    id="insights.comparison-period"
                    values={{ dateRange: formattedPreviousDateRange }}
                  />
                </PreviousPeriod>
              )}
            </DateRoot>
          </Popover>
          <RightControls>
            {!isLoading && isReportsEnabled && insightEntity.type !== 'venue' && (
              <Button variant="blue" size="small" onClick={handleNavigateToCreateReport}>
                <FormattedMessage id="insights.reports.header" />
              </Button>
            )}
            <Popover
              content={<InsightSettings isVATEnabled={isVATEnabled} setIsVATEnabled={setIsVATEnabled} />}
              showOnHover={false}
              showOnClick
              placement="bottom"
            >
              <IconButton variant="blue" size="small">
                <Settings />
              </IconButton>
            </Popover>
          </RightControls>
        </InsightPageControlsContainer>
      )}
    </>
  );
};
